import { ClampToEdgeWrapping, DataTexture, LinearFilter, LinearMipmapLinearFilter, RGBAFormat, SRGBColorSpace, Texture, UnsignedByteType } from "three";
import type { RasterWindow } from "../io/geoRaster";

/**
 * Converts a raster window into an RGBA texture.
 *
 * GeoTIFF pixel data arrives interleaved with 1–4 samples; WebGL wants tightly packed
 * RGBA, so the conversion happens once here rather than in a shader.
 */
export function textureFromRaster(
  window: RasterWindow,
  options: { hasAlpha?: boolean; flipY?: boolean } = {},
): DataTexture {
  const { width, height, samples } = window;
  const source = window.data as unknown as ArrayLike<number>;
  const rgba = new Uint8Array(width * height * 4);
  const flip = options.flipY !== false;

  for (let y = 0; y < height; y++) {
    // Rasters are stored north-up (row 0 = top), whereas a texture's V axis runs bottom-up.
    const sourceRow = flip ? height - 1 - y : y;
    for (let x = 0; x < width; x++) {
      const from = (sourceRow * width + x) * samples;
      const to = (y * width + x) * 4;
      if (samples >= 3) {
        rgba[to] = source[from];
        rgba[to + 1] = source[from + 1];
        rgba[to + 2] = source[from + 2];
        rgba[to + 3] = samples >= 4 && options.hasAlpha !== false ? source[from + 3] : 255;
      } else {
        // Single band or grey+alpha: replicate the grey across RGB.
        const grey = source[from];
        rgba[to] = grey;
        rgba[to + 1] = grey;
        rgba[to + 2] = grey;
        rgba[to + 3] = samples === 2 && options.hasAlpha !== false ? source[from + 1] : 255;
      }
    }
  }

  const texture = new DataTexture(rgba, width, height, RGBAFormat, UnsignedByteType);
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 8;
  texture.needsUpdate = true;
  return texture;
}

/** Colour stops for elevation shading, dark blue-green through to snow white. */
const ELEVATION_RAMP: Array<[number, number, number, number]> = [
  [0.0, 42, 79, 110],
  [0.15, 58, 122, 96],
  [0.35, 122, 158, 79],
  [0.55, 191, 175, 92],
  [0.75, 176, 124, 84],
  [0.9, 158, 148, 148],
  [1.0, 245, 247, 250],
];

function rampColor(t: number): [number, number, number] {
  const clamped = Math.min(1, Math.max(0, t));
  for (let i = 1; i < ELEVATION_RAMP.length; i++) {
    const [stop, r, g, b] = ELEVATION_RAMP[i];
    const [prevStop, pr, pg, pb] = ELEVATION_RAMP[i - 1];
    if (clamped <= stop) {
      const f = stop === prevStop ? 0 : (clamped - prevStop) / (stop - prevStop);
      return [pr + (r - pr) * f, pg + (g - pg) * f, pb + (b - pb) * f];
    }
  }
  const last = ELEVATION_RAMP[ELEVATION_RAMP.length - 1];
  return [last[1], last[2], last[3]];
}

/**
 * Builds a hypsometric texture from elevation samples, with a hillshade multiplied in.
 *
 * Used when only a surface model is loaded: a raw grey ramp makes it very hard to read
 * terrain, whereas ramp + relief shading shows structure immediately.
 */
export function textureFromElevation(
  values: Float32Array,
  width: number,
  height: number,
  options: {
    min: number;
    max: number;
    /** Cell size in metres, needed for a correct hillshade slope. */
    cellSize: number;
    noData?: number | null;
    sunAzimuthDeg?: number;
    sunElevationDeg?: number;
    hillshade?: number;
  },
): DataTexture {
  const { min, max, cellSize } = options;
  const range = max - min || 1;
  const azimuth = ((options.sunAzimuthDeg ?? 315) * Math.PI) / 180;
  const zenith = ((90 - (options.sunElevationDeg ?? 45)) * Math.PI) / 180;
  const shadeMix = options.hillshade ?? 0.55;
  const rgba = new Uint8Array(width * height * 4);

  const at = (x: number, y: number): number => {
    const cx = Math.min(width - 1, Math.max(0, x));
    const cy = Math.min(height - 1, Math.max(0, y));
    return values[cy * width + cx];
  };

  for (let y = 0; y < height; y++) {
    const sourceRow = height - 1 - y;
    for (let x = 0; x < width; x++) {
      const to = (y * width + x) * 4;
      const value = values[sourceRow * width + x];
      if (!Number.isFinite(value)) {
        rgba[to + 3] = 0;
        continue;
      }
      const [r, g, b] = rampColor((value - min) / range);

      // Horn's method for the surface gradient over the 3x3 neighbourhood.
      const a = at(x - 1, sourceRow - 1), bb = at(x, sourceRow - 1), c = at(x + 1, sourceRow - 1);
      const d = at(x - 1, sourceRow), f = at(x + 1, sourceRow);
      const g0 = at(x - 1, sourceRow + 1), h = at(x, sourceRow + 1), i = at(x + 1, sourceRow + 1);
      let shade = 1;
      if ([a, bb, c, d, f, g0, h, i].every(Number.isFinite)) {
        const dzdx = (c + 2 * f + i - (a + 2 * d + g0)) / (8 * cellSize);
        const dzdy = (g0 + 2 * h + i - (a + 2 * bb + c)) / (8 * cellSize);
        const slope = Math.atan(Math.hypot(dzdx, dzdy));
        const aspect = Math.atan2(dzdy, -dzdx);
        shade =
          Math.cos(zenith) * Math.cos(slope) +
          Math.sin(zenith) * Math.sin(slope) * Math.cos(azimuth - aspect);
        shade = Math.min(1, Math.max(0, shade));
      }
      const mix = 1 - shadeMix + shadeMix * shade * 1.25;
      rgba[to] = Math.min(255, r * mix);
      rgba[to + 1] = Math.min(255, g * mix);
      rgba[to + 2] = Math.min(255, b * mix);
      rgba[to + 3] = 255;
    }
  }

  const texture = new DataTexture(rgba, width, height, RGBAFormat, UnsignedByteType);
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}

/** Rough GPU cost of a texture including its mip chain. */
export function textureBytes(texture: Texture): number {
  const image = texture.image as { width?: number; height?: number } | undefined;
  const width = image?.width ?? 0;
  const height = image?.height ?? 0;
  const base = width * height * 4;
  return texture.generateMipmaps ? Math.round(base * 1.34) : base;
}
